import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import {
  Card,
  CardHeader,
  CardBody,
  Typography,
} from '@statsbomb/kitbag-components';
// eslint-disable-next-line import/no-unresolved
import { AxesChart, AxisFormat } from '@statsbomb/kitbag-datavis';
import { getPalette } from '../../../utils/visualisations/visPalettes';
import { buildEpaRaceData } from './EpaRaceChart.dataManipulation';
import { getScoringPlays } from './ScoringPlays.dataManipulation';
import { OffenseCell, ScoringPlaysEmptyState } from './GameOverview.styles';
import TeamIcon from './TeamIcon';
import { EpaRaceChartWrap, Legend, ChartCanvas } from './EpaRaceChart.styles';

// AxesChartCanvasTypes enum isn't re-exported from the package's main entry —
// the enum values are just these strings.
const LINE_CANVAS = 'line';
const GUIDE_CANVAS = 'guide';

// Fixed chart dimensions sized for the bento right column (1/3 width).
// SVG CSS `max-width: 100%` in styles lets it scale down if the column is
// narrower than CHART_WIDTH on smaller viewports.
const CHART_WIDTH = 420;
const CHART_HEIGHT = 260;

function EpaRaceChart({
  plays,
  game,
  isHomeTeam,
  selectedTeamColor,
  opponentTeamColor,
}) {
  const theme = useTheme();
  const visPalette = getPalette(theme.isDark);

  // Derived data is memoised so the chart gets stable references between
  // renders instead of redrawing on every parent render.
  const { home, away } = useMemo(
    () => buildEpaRaceData(plays, game),
    [plays, game],
  );

  // Vertical guide line at each scoring play's gameIndex
  // (XPs excluded via getScoringPlays).
  const scoringGuides = useMemo(
    () =>
      getScoringPlays(plays, game)
        .filter((sp) => sp.raw?.gameIndex != null)
        .map((sp) => ({
          id: `scoring-${sp.id}`,
          valueX: sp.raw.gameIndex,
          valueY: 0,
          fullY: true,
        })),
    [plays, game],
  );

  if (!game?.homeTeam || !game?.awayTeam) return null;
  if (!home.length && !away.length) return null;

  // T1-only games have no EPA (it is nulled upstream). Plotting them would
  // draw two flat lines at zero, which reads as "no advantage" rather than
  // "no data", so say so instead.
  const hasEpa = plays.some((p) => p.scrimmageEpa != null);
  if (!hasEpa) {
    return (
      <EpaRaceChartWrap>
        <Card>
          <CardHeader>
            <Typography variant="headingMedium">EPA Timeline</Typography>
          </CardHeader>
          <CardBody>
            <ScoringPlaysEmptyState data-testid="epa-unavailable">
              EPA is not available for this game.
            </ScoringPlaysEmptyState>
          </CardBody>
        </Card>
      </EpaRaceChartWrap>
    );
  }

  const formatFinal = (series) =>
    (series[series.length - 1]?.valueY ?? 0).toFixed(2);
  const chartLabel =
    `EPA timeline, cumulative expected points added by play. ` +
    `Final: ${game.awayTeam.name} ${formatFinal(away)}, ` +
    `${game.homeTeam.name} ${formatFinal(home)}.`;

  // Flip the Y axis so positive EPA renders above the origin, negative below.
  // GUIDE_CANVAS first expresses the intended layering (guides = background
  // reference, team lines = foreground data). The library renders guides on
  // top regardless of array position; CSS in EpaRaceChart.styles.js dims the
  // guide <line> elements so team lines visually dominate.
  const canvasSetups = [
    {
      type: GUIDE_CANVAS,
      data: scoringGuides,
      domainY: { reverse: true },
      lineConfig: {
        // Light: weak ink, visible on the card. Dark: the weak layering shade,
        // the same blue-grey as weak ink but a step darker so the guides sit
        // behind the team lines. (The canvas shades are near-identical to the
        // card background.) strokeWidth 0.75 keeps them thin enough that the
        // thicker solid team lines visually dominate even when a guide
        // crosses them.
        stroke: theme.isDark
          ? theme.colours.layering[0].weak
          : theme.colours.ink.primary.weak,
        strokeWidth: 0.75,
        strokeDash: '4,4',
      },
    },
    {
      type: LINE_CANVAS,
      data: home,
      colour: isHomeTeam ? selectedTeamColor : opponentTeamColor,
      domainY: { reverse: true },
    },
    {
      type: LINE_CANVAS,
      data: away,
      colour: isHomeTeam ? opponentTeamColor : selectedTeamColor,
      domainY: { reverse: true },
    },
  ];

  const bottomAxis = { label: 'Play #', format: AxisFormat.NUMBER };
  const leftAxis = { label: 'Cumulative EPA', format: AxisFormat.NUMBER };

  // Same team icon as the Scoreboard's Team column: the selected team gets
  // the left half-moon, the opponent the top one.
  const legendContext = {
    selectedTeamId: isHomeTeam ? game.homeTeam.id : game.awayTeam.id,
    selectedTeamColor,
    opponentTeamColor,
  };

  return (
    <EpaRaceChartWrap>
      <Card>
        <CardHeader>
          <Typography variant="headingMedium">EPA Timeline</Typography>
        </CardHeader>
        <CardBody>
          <ChartCanvas
            $axisColor={visPalette.axis}
            role="img"
            aria-label={chartLabel}
          >
            <AxesChart
              canvasSetups={canvasSetups}
              chartConfig={{
                chartWidth: CHART_WIDTH,
                chartHeight: CHART_HEIGHT,
              }}
              bottomAxis={bottomAxis}
              leftAxis={leftAxis}
            />
          </ChartCanvas>
          <Legend>
            {/* Selected team first, matching the page header. */}
            {(isHomeTeam
              ? [game.homeTeam, game.awayTeam]
              : [game.awayTeam, game.homeTeam]
            ).map((team) => (
              <OffenseCell key={team.id}>
                <TeamIcon team={team} context={legendContext} />
                {team.name}
              </OffenseCell>
            ))}
          </Legend>
        </CardBody>
      </Card>
    </EpaRaceChartWrap>
  );
}

EpaRaceChart.propTypes = {
  plays: PropTypes.arrayOf(PropTypes.object),
  game: PropTypes.shape({
    homeTeam: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      shortName: PropTypes.string,
      colorPrimary: PropTypes.string,
      colorSecondary: PropTypes.string,
    }),
    awayTeam: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      shortName: PropTypes.string,
      colorPrimary: PropTypes.string,
      colorSecondary: PropTypes.string,
    }),
  }),
  isHomeTeam: PropTypes.bool,
  selectedTeamColor: PropTypes.string,
  opponentTeamColor: PropTypes.string,
};

EpaRaceChart.defaultProps = {
  plays: [],
  game: null,
  isHomeTeam: true,
  selectedTeamColor: null,
  opponentTeamColor: null,
};

export default EpaRaceChart;
